import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  type ListNotesUrlParams,
  NoteResponse,
  type NoteUpsertInput as NoteUpsertInputSchema,
} from '@listening/api-contract';
import { AsyncResult, Atom, Reactivity } from 'effect/reactivity';
import { Effect, Schema } from 'effect';
import {
  apiMutation,
  pollEvery,
  ssrApiQuery,
  withOptimisticUpdate,
} from './api-runtime';
import type { ReactivityKey } from './reactivity-keys';

// Row shape: the list endpoint's `NoteResponse` (id, title, body,
// audio_conversion_id, …), derived from the API contract.
export type NoteRow = typeof NoteResponse.Type;

// —— Effect programs ——

// The ListNotes urlParams minus `cursor_id`, which the pager threads in
// separately (see listNotesPageEffect).
type ListNotesParams = Omit<typeof ListNotesUrlParams.Type, 'cursor_id'>;

const listNotesPageEffect = (
  params: ListNotesParams & { cursor_id?: string } = {},
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.ListNotes({ query: params }).pipe(
      Effect.map((r) => ({
        items: r.data.notes.map((n) => ({ ...n })),
        nextCursor: r.data.cursor_id ?? undefined,
      })),
    ),
  );

// Cursor-follow until exhausted. The single list query drains all pages so
// every view (filtered by audio_conversion_id client-side via `select`) sees
// every note for the current user.
export const listNotesEffect = (params: ListNotesParams = {}) =>
  Effect.gen(function* () {
    const all: Array<NoteRow> = [];
    let cursor: string | undefined;
    do {
      const page = yield* listNotesPageEffect({
        ...params,
        ...(cursor === undefined ? {} : { cursor_id: cursor }),
      });
      all.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return all;
  });

// Single-note upsert input: the contract's NoteUpsertInput schema plus the
// client-only `user_id` (the server derives it from auth, but a callsite in
// notes-service.ts still includes it).
export type NoteUpsertInput = typeof NoteUpsertInputSchema.Type & {
  user_id?: string;
};

export const upsertNotesEffect = (notes: ReadonlyArray<NoteUpsertInput>) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.UpsertManyNotes({ payload: { notes } }).pipe(
      Effect.map((r) => r.data.map((n) => ({ ...n }))),
    ),
  );

export const deleteNoteEffect = (noteId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.DeleteNote({ params: { noteId } }),
  );

// —— Atoms ——

export const notesKeys = {
  list: 'notes:list',
} as const satisfies Record<string, ReactivityKey>;

// Notes created on another device surface in the read-along sidebar without a
// manual refresh; polling pauses while the app is in the background.
const notesPollIntervalMs = 5 * 60 * 1000;

/** Every note the viewer has. The list drains every page, so filtering it by
 * audio sees them all. A page the server renders sends them along. */
export const notesAtom = ssrApiQuery(listNotesEffect(), {
  key: 'notes',
  success: Schema.mutable(Schema.Array(NoteResponse)),
  staleTime: notesPollIntervalMs,
  reactivityKeys: [notesKeys.list],
}).pipe(pollEvery<Array<NoteRow>>(() => notesPollIntervalMs));

/** The viewer's notes on one audio. */
export const audioNotesAtom = Atom.family((audioId: string) =>
  Atom.make((get) =>
    AsyncResult.map(get(notesAtom), (notes) =>
      notes.filter((note) => note.audio_conversion_id === audioId),
    ),
  ),
);

const settleNotes = Effect.ensuring(Reactivity.invalidate([notesKeys.list]));

/** Saves an edit to an existing note, showing it at once. The anchor travels
 * with it so an edit never drops it; the refetch reconciles any server-side
 * changes. */
export const updateNoteAtom = apiMutation((next: NoteRow) =>
  withOptimisticUpdate(
    notesAtom,
    (notes) => notes.map((note) => (note.id === next.id ? next : note)),
    upsertNotesEffect([
      {
        id: next.id,
        title: next.title,
        body: next.body,
        audio_conversion_id: next.audio_conversion_id,
        chapter_id: next.chapter_id,
        chapter_audio_offset_ms: next.chapter_audio_offset_ms,
        chapter_word_offset: next.chapter_word_offset,
        tts_id: next.tts_id,
        doc_pack_page: next.doc_pack_page,
        doc_pack_char_start: next.doc_pack_char_start,
        doc_pack_char_end: next.doc_pack_char_end,
      },
    ]),
  ).pipe(settleNotes),
);

export const deleteNoteAtom = apiMutation((noteId: string) =>
  withOptimisticUpdate(
    notesAtom,
    (notes) => notes.filter((note) => note.id !== noteId),
    deleteNoteEffect(noteId),
  ).pipe(settleNotes),
);

/** Creates or saves notes. The server assigns new notes their ids, so they
 * show once the list refetches. */
export const upsertNotesAtom = apiMutation(
  (notes: ReadonlyArray<NoteUpsertInput>) => upsertNotesEffect(notes),
  { reactivityKeys: [notesKeys.list] },
);
